import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Phone,
  Mail,
  Users,
  Calendar,
  Clock,
  Check,
  X as XIcon,
  Trash2,
  RefreshCw,
  Search,
  ExternalLink,
  CalendarCheck,
  UtensilsCrossed,
  Settings as SettingsIcon,
  Images as ImagesIcon,
} from "lucide-react";
import AdminMenu from "./AdminMenu";
import AdminSettings from "./AdminSettings";
import AdminGallery from "./AdminGallery";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  pending: { label: "En attente", color: "text-yellow-400 border-yellow-400/40 bg-yellow-400/5" },
  confirmed: { label: "Confirmée", color: "text-emerald-400 border-emerald-400/40 bg-emerald-400/5" },
  cancelled: { label: "Annulée", color: "text-red-400 border-red-400/40 bg-red-400/5" },
};

const Stat = ({ label, value, accent = false, testid }) => (
  <div
    data-testid={testid}
    className={`bg-surface border ${accent ? "border-copper" : "border-edge"} p-6`}
  >
    <p className="overline mb-3">{label}</p>
    <p
      className={`font-serif text-4xl font-light ${accent ? "text-copper" : "text-ivory"}`}
    >
      {value}
    </p>
  </div>
);

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-[0.2em] px-3 py-1 border ${meta.color}`}
    >
      {meta.label}
    </span>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("grizzly_admin_token");
  const adminEmail = localStorage.getItem("grizzly_admin_email");

  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [section, setSection] = useState("reservations");

  const authHeader = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const logout = useCallback(() => {
    localStorage.removeItem("grizzly_admin_token");
    localStorage.removeItem("grizzly_admin_email");
    navigate("/admin/login");
  }, [navigate]);

  const fetchAll = useCallback(async () => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    setLoading(true);
    try {
      const [resvRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/reservations`, { headers: authHeader }),
        axios.get(`${API}/admin/stats`, { headers: authHeader }),
      ]);
      setReservations(resvRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, [token, authHeader, navigate, logout]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      const res = await axios.patch(
        `${API}/admin/reservations/${id}`,
        { status },
        { headers: authHeader },
      );
      setReservations((rs) => rs.map((r) => (r.id === id ? res.data : r)));
      // refresh stats
      const sr = await axios.get(`${API}/admin/stats`, { headers: authHeader });
      setStats(sr.data);
    } catch (err) {
      console.error(err);
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteResv = async (id) => {
    if (!window.confirm("Supprimer définitivement cette réservation ?")) return;
    setUpdatingId(id);
    try {
      await axios.delete(`${API}/admin/reservations/${id}`, {
        headers: authHeader,
      });
      setReservations((rs) => rs.filter((r) => r.id !== id));
      const sr = await axios.get(`${API}/admin/stats`, { headers: authHeader });
      setStats(sr.data);
    } catch (err) {
      console.error(err);
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = reservations.filter((r) => {
    if (filter !== "all" && r.status !== filter) return false;
    if (query) {
      const q = query.toLowerCase();
      if (
        !r.name.toLowerCase().includes(q) &&
        !r.email.toLowerCase().includes(q) &&
        !r.phone.toLowerCase().includes(q) &&
        !r.date.includes(q)
      )
        return false;
    }
    return true;
  });

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-obsidian text-ivory"
    >
      {/* Header */}
      <header className="border-b border-edge bg-obsidian/95 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-2xl text-ivory">Grizzly</span>
            <span className="overline hidden sm:inline">Administration</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              data-testid="open-public-site"
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ivory/55 hover:text-copper transition-colors"
            >
              Voir le site <ExternalLink size={12} />
            </a>
            <span className="hidden md:inline text-xs text-ivory/40 truncate max-w-[200px]">
              {adminEmail}
            </span>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ivory/70 hover:text-copper transition-colors"
            >
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        {/* Title */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="divider-line" />
            <span className="overline">Tableau de bord</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">
            Administration
          </h1>
        </div>

        {/* Section tabs */}
        <div className="flex gap-2 mb-10 border-b border-edge">
          <button
            data-testid="section-tab-reservations"
            onClick={() => setSection("reservations")}
            className={`flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-[0.18em] transition-all border-b-2 -mb-px ${
              section === "reservations"
                ? "border-copper text-copper"
                : "border-transparent text-ivory/55 hover:text-ivory"
            }`}
          >
            <CalendarCheck size={14} /> Réservations
          </button>
          <button
            data-testid="section-tab-menu"
            onClick={() => setSection("menu")}
            className={`flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-[0.18em] transition-all border-b-2 -mb-px ${
              section === "menu"
                ? "border-copper text-copper"
                : "border-transparent text-ivory/55 hover:text-ivory"
            }`}
          >
            <UtensilsCrossed size={14} /> Menu
          </button>
          <button
            data-testid="section-tab-gallery"
            onClick={() => setSection("gallery")}
            className={`flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-[0.18em] transition-all border-b-2 -mb-px ${
              section === "gallery"
                ? "border-copper text-copper"
                : "border-transparent text-ivory/55 hover:text-ivory"
            }`}
          >
            <ImagesIcon size={14} /> Galerie
          </button>
          <button
            data-testid="section-tab-settings"
            onClick={() => setSection("settings")}
            className={`flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-[0.18em] transition-all border-b-2 -mb-px ${
              section === "settings"
                ? "border-copper text-copper"
                : "border-transparent text-ivory/55 hover:text-ivory"
            }`}
          >
            <SettingsIcon size={14} /> Paramètres
          </button>
        </div>

        {section === "menu" ? (
          <AdminMenu />
        ) : section === "gallery" ? (
          <AdminGallery />
        ) : section === "settings" ? (
          <AdminSettings />
        ) : (
          <ReservationsSection
            stats={stats}
            loading={loading}
            filtered={filtered}
            query={query}
            setQuery={setQuery}
            filter={filter}
            setFilter={setFilter}
            updatingId={updatingId}
            updateStatus={updateStatus}
            deleteResv={deleteResv}
            fetchAll={fetchAll}
          />
        )}
      </main>
    </div>
  );
};

const ReservationsSection = ({
  stats,
  loading,
  filtered,
  query,
  setQuery,
  filter,
  setFilter,
  updatingId,
  updateStatus,
  deleteResv,
  fetchAll,
}) => {
  return (
    <>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
        <h2 className="font-serif text-3xl font-light tracking-tight">
          Réservations
        </h2>
        <button
          data-testid="refresh-button"
          onClick={fetchAll}
          disabled={loading}
          className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-copper hover:text-ivory transition-colors"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Actualiser
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-12">
        <Stat
          label="À venir"
          value={stats?.upcoming ?? "—"}
          accent
          testid="stat-upcoming"
        />
        <Stat label="En attente" value={stats?.pending ?? "—"} testid="stat-pending" />
        <Stat label="Confirmées" value={stats?.confirmed ?? "—"} testid="stat-confirmed" />
        <Stat label="Annulées" value={stats?.cancelled ?? "—"} testid="stat-cancelled" />
        <Stat label="Total" value={stats?.total ?? "—"} testid="stat-total" />
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
        <div className="flex gap-2 flex-wrap">
          {[
            { v: "all", label: "Toutes" },
            { v: "pending", label: "En attente" },
            { v: "confirmed", label: "Confirmées" },
            { v: "cancelled", label: "Annulées" },
          ].map((f) => (
            <button
              key={f.v}
              data-testid={`filter-${f.v}`}
              onClick={() => setFilter(f.v)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-all ${
                filter === f.v
                  ? "border-copper text-copper bg-copper/5"
                  : "border-edge text-ivory/55 hover:text-ivory hover:border-ivory/30"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex-1 md:max-w-md relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ivory/40"
          />
          <input
            data-testid="reservation-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher nom, email, téléphone, date..."
            className="w-full bg-surface border border-edge px-10 py-2.5 text-sm text-ivory placeholder:text-ivory/35 focus:outline-none focus:border-copper transition-colors"
          />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-20 text-ivory/50">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="empty-state"
          className="text-center py-24 border border-dashed border-edge"
        >
          <p className="font-serif text-2xl text-ivory/55 mb-2">
            Aucune réservation
          </p>
          <p className="text-sm text-ivory/35">
            {query || filter !== "all"
              ? "Essayez d'autres filtres."
              : "Les nouvelles réservations apparaîtront ici."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((r) => (
            <div
              key={r.id}
              data-testid={`reservation-row-${r.id}`}
              className="bg-surface border border-edge hover:border-edge/80 p-5 md:p-6 grid md:grid-cols-12 gap-4 items-start"
            >
              {/* Date / time */}
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 text-copper mb-1.5">
                  <Calendar size={14} />
                  <span className="text-sm font-serif">{r.date}</span>
                </div>
                <div className="flex items-center gap-2 text-ivory/70">
                  <Clock size={14} />
                  <span className="text-sm">{r.time}</span>
                </div>
              </div>

              {/* Name + guests */}
              <div className="md:col-span-3">
                <p className="font-serif text-xl text-ivory leading-tight">
                  {r.name}
                </p>
                <div className="flex items-center gap-1.5 text-ivory/55 mt-1.5 text-sm">
                  <Users size={12} /> {r.guests} pers.
                </div>
              </div>

              {/* Contact */}
              <div className="md:col-span-3 space-y-1.5">
                <a
                  href={`tel:${r.phone}`}
                  className="flex items-center gap-2 text-sm text-ivory/80 hover:text-copper transition-colors"
                >
                  <Phone size={12} /> {r.phone}
                </a>
                <a
                  href={`mailto:${r.email}`}
                  className="flex items-center gap-2 text-xs text-ivory/55 hover:text-copper transition-colors truncate"
                >
                  <Mail size={12} /> {r.email}
                </a>
                {r.notes && (
                  <p className="text-xs text-ivory/45 italic mt-2 line-clamp-2">
                    « {r.notes} »
                  </p>
                )}
              </div>

              {/* Status + actions */}
              <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
                <StatusPill status={r.status} />
                <div className="flex gap-2 flex-wrap md:justify-end">
                  {r.status !== "confirmed" && (
                    <button
                      data-testid={`confirm-${r.id}`}
                      onClick={() => updateStatus(r.id, "confirmed")}
                      disabled={updatingId === r.id}
                      className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] px-3 py-1.5 border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10 transition-colors disabled:opacity-50"
                    >
                      <Check size={12} /> Confirmer
                    </button>
                  )}
                  {r.status !== "cancelled" && (
                    <button
                      data-testid={`cancel-${r.id}`}
                      onClick={() => updateStatus(r.id, "cancelled")}
                      disabled={updatingId === r.id}
                      className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] px-3 py-1.5 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-50"
                    >
                      <XIcon size={12} /> Annuler
                    </button>
                  )}
                  <button
                    data-testid={`delete-${r.id}`}
                    onClick={() => deleteResv(r.id)}
                    disabled={updatingId === r.id}
                    className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] px-3 py-1.5 border border-edge text-ivory/55 hover:text-ivory hover:border-ivory/40 transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default AdminDashboard;
